import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import "../styles/app.css";
import "../styles/auth.css";

export default function Security() {
  const {
    user,
    loading,
    isAuthenticated,
    logout,
    beginOptionalTotpSetup,
    confirmOptionalTotpSetup,
    getMfaStatus,
    disableOptionalTotp,
  } = useAuth();

  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const refreshStatus = useCallback(async () => {
    const pref = await getMfaStatus();
    setStatus(pref);
  }, [getMfaStatus]);

  useEffect(() => {
    if (!isAuthenticated) return;
    refreshStatus();
  }, [isAuthenticated, refreshStatus]);

  if (loading) {
    return (
      <div className="app-shell">
        <main className="app-main">
          <p className="leads-support">Loading…</p>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  const totpPreferred = status?.preferred === "TOTP";
  const totpEnabled =
    totpPreferred ||
    status?.enabled?.includes?.("TOTP") ||
    status?.totp === "PREFERRED" ||
    status?.totp === "ENABLED";

  async function startSetup(e) {
    e.preventDefault();
    setError("");
    setInfo("");
    setBusy(true);
    try {
      const details = await beginOptionalTotpSetup();
      setSetup(details);
      setInfo("Scan or enter the key in your authenticator, then verify a code.");
    } catch (err) {
      setError(err?.message || "Could not start MFA setup.");
    } finally {
      setBusy(false);
    }
  }

  async function verifySetup(e) {
    e.preventDefault();
    setError("");
    setInfo("");
    setBusy(true);
    try {
      await confirmOptionalTotpSetup(code);
      setSetup(null);
      setCode("");
      setInfo("Authenticator MFA is on. You’ll be asked for a code at sign-in.");
      await refreshStatus();
    } catch (err) {
      setError(
        err?.name === "EnableSoftwareTokenMFAException" ||
          err?.name === "CodeMismatchException"
          ? "Invalid code. Use a fresh 6-digit code from the authenticator."
          : err?.message || "Could not verify MFA."
      );
    } finally {
      setBusy(false);
    }
  }

  async function turnOff(e) {
    e.preventDefault();
    if (!window.confirm("Turn off authenticator MFA for this account?")) return;
    setError("");
    setInfo("");
    setBusy(true);
    try {
      await disableOptionalTotp();
      setSetup(null);
      setInfo("Authenticator MFA turned off.");
      await refreshStatus();
    } catch (err) {
      setError(err?.message || "Could not disable MFA.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link className="app-header__brand" to="/">
          FTOJ
        </Link>
        <div className="app-header__meta">
          <Link to="/leads">Inbox</Link>
          <span>{user?.signInDetails?.loginId || user?.username}</span>
          <button className="btn btn--ghost" type="button" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </header>

      <main className="app-main">
        <div className="leads-heading">
          <div>
            <h1 className="leads-title">Security</h1>
            <p className="leads-support">
              Optional authenticator MFA. You can turn this on for an extra sign-in
              step with an authenticator app.
            </p>
          </div>
        </div>

        {error ? <p className="auth-error">{error}</p> : null}
        {info ? <p className="auth-info">{info}</p> : null}

        <section className="leads-panel">
          <h2>Authenticator MFA</h2>
          <p>
            Status:{" "}
            <strong>{totpEnabled ? "Enabled" : "Off (optional)"}</strong>
          </p>

          {!setup && !totpEnabled ? (
            <form onSubmit={startSetup}>
              <button className="btn btn--primary" type="submit" disabled={busy}>
                {busy ? "Working…" : "Enable authenticator MFA"}
              </button>
            </form>
          ) : null}

          {setup ? (
            <form className="auth-form" onSubmit={verifySetup}>
              {setup.setupUri ? (
                <p className="auth-hint">
                  <a href={setup.setupUri}>Open authenticator setup link</a>
                </p>
              ) : null}
              <label>
                Manual key
                <input
                  type="text"
                  readOnly
                  value={setup.sharedSecret}
                  onFocus={(e) => e.target.select()}
                />
              </label>
              <label>
                6-digit code
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                />
              </label>
              <button className="btn btn--primary" type="submit" disabled={busy}>
                {busy ? "Verifying…" : "Verify & enable"}
              </button>
            </form>
          ) : null}

          {totpEnabled && !setup ? (
            <form onSubmit={turnOff}>
              <button className="btn btn--ghost" type="submit" disabled={busy}>
                Turn off MFA
              </button>
            </form>
          ) : null}
        </section>
      </main>
    </div>
  );
}
